import type { FathomStore, Actual, Estimate, Reconciliation } from "@fathom/store";
import type { ReportConfig, ReportData } from "./types.js";

/**
 * Collect and aggregate report data from the store.
 */
export async function collectReportData(
  store: FathomStore,
  config: ReportConfig,
): Promise<ReportData> {
  const [estimates, actuals, reconciliations, benchmarks, snapshots, pricing] =
    await Promise.all([
      store.getEstimates(config.projectId),
      store.getActuals(config.projectId),
      store.getReconciliations(config.projectId),
      store.getBenchmarks(),
      store.getSnapshots({ projectId: config.projectId }),
      store.getPricing(),
    ]);

  // Filter actuals by date range if specified
  const filteredActuals = config.dateRange
    ? actuals.filter(
        (a) =>
          a.timestamp >= config.dateRange!.start &&
          a.timestamp <= config.dateRange!.end,
      )
    : actuals;

  // Build pricing lookup
  const pricingMap = new Map(pricing.map((p) => [p.model, p]));

  // Get latest estimate for feature info
  const latestEstimate =
    estimates.length > 0
      ? estimates.reduce((a, b) => (a.createdAt > b.createdAt ? a : b))
      : null;

  // Get latest reconciliation for accuracy data
  const latestRecon =
    reconciliations.length > 0
      ? reconciliations.reduce((a, b) => (a.createdAt > b.createdAt ? a : b))
      : null;

  // Build feature data
  const features = buildFeatureData(
    latestEstimate,
    latestRecon,
    filteredActuals,
    pricingMap,
  );

  // Calculate cost breakdown
  const costBreakdown = buildCostBreakdown(filteredActuals, pricingMap);

  // Calculate velocity
  const velocity = buildVelocityData(features, snapshots);

  // Calculate estimate accuracy
  const estimateAccuracy = buildEstimateAccuracy(latestRecon, features);

  // Calculate totals
  const totalCost = costBreakdown.byModel.reduce((sum, m) => sum + m.cost, 0);
  const totalTokens = filteredActuals.reduce(
    (sum, a) => sum + a.inputTokens + a.outputTokens,
    0,
  );
  const totalEstimatedCost = latestEstimate
    ? latestEstimate.totalEstimate.cost
    : 0;

  const featuresComplete = features.filter(
    (f) => f.status === "complete",
  ).length;
  const featuresInProgress = features.filter(
    (f) => f.status === "in_progress",
  ).length;

  // Determine project start date
  const projectStartTs =
    filteredActuals.length > 0
      ? Math.min(...filteredActuals.map((a) => a.sessionStart))
      : Date.now();

  const now = new Date();
  const reportDate = formatDate(now);
  const startDate = formatDate(new Date(projectStartTs));

  return {
    project: {
      name: config.projectName,
      startDate,
      reportDate,
    },
    summary: {
      featuresTotal: features.length,
      featuresComplete,
      featuresInProgress,
      totalTokens,
      totalCost,
      budgetUsed:
        totalEstimatedCost > 0
          ? Math.round((totalCost / totalEstimatedCost) * 100)
          : 0,
      estimateAccuracy: estimateAccuracy.overall,
    },
    features,
    costBreakdown,
    velocity,
    estimateAccuracy,
  };
}

// ── Helpers ──────────────────────────────────────────────────

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function calculateCost(
  actual: Actual,
  pricingMap: Map<string, { inputPerMTok: number; outputPerMTok: number; cacheReadPerMTok: number }>,
): number {
  const p = pricingMap.get(actual.model);
  if (!p) {
    // Fall back to the estimatedCost stored on the actual
    return actual.estimatedCost;
  }
  return (
    (actual.inputTokens / 1_000_000) * p.inputPerMTok +
    (actual.outputTokens / 1_000_000) * p.outputPerMTok +
    (actual.cacheReadTokens / 1_000_000) * p.cacheReadPerMTok
  );
}

interface FeatureAccumulator {
  name: string;
  status: "complete" | "in_progress" | "pending";
  complexity: string;
  estimatedCost: number;
  actualCost: number;
  sessions: number;
  startTime: number;
  endTime: number;
}

function buildFeatureData(
  estimate: Estimate | null,
  recon: Reconciliation | null,
  actuals: Actual[],
  pricingMap: Map<string, { inputPerMTok: number; outputPerMTok: number; cacheReadPerMTok: number }>,
): ReportData["features"] {
  const featureMap = new Map<string, FeatureAccumulator>();

  // Seed from estimate features
  if (estimate) {
    for (const f of estimate.features) {
      featureMap.set(f.id, {
        name: f.name,
        status: "pending",
        complexity: f.complexity,
        estimatedCost: f.estimatedCost,
        actualCost: 0,
        sessions: 0,
        startTime: Infinity,
        endTime: 0,
      });
    }
  }

  // Update from reconciliation deltas (has status + accuracy)
  if (recon) {
    for (const delta of recon.featureDeltas) {
      const existing = featureMap.get(delta.featureId);
      if (existing) {
        existing.status =
          delta.sessionsCount > 0 ? "in_progress" : existing.status;
      }
    }
  }

  // Accumulate actuals per feature
  for (const actual of actuals) {
    if (!actual.featureId) continue;
    const feature = featureMap.get(actual.featureId);
    if (!feature) {
      // Feature in actuals but not in estimate — create entry
      featureMap.set(actual.featureId, {
        name: actual.featureId,
        status: "in_progress",
        complexity: "M",
        estimatedCost: 0,
        actualCost: calculateCost(actual, pricingMap),
        sessions: 1,
        startTime: actual.sessionStart,
        endTime: actual.sessionEnd ?? actual.sessionStart,
      });
      continue;
    }
    feature.actualCost += calculateCost(actual, pricingMap);
    feature.sessions += 1;
    feature.status = "in_progress";
    feature.startTime = Math.min(feature.startTime, actual.sessionStart);
    feature.endTime = Math.max(
      feature.endTime,
      actual.sessionEnd ?? actual.sessionStart,
    );
  }

  // Mark features as complete if actual cost meets/exceeds estimate (heuristic)
  // or if reconciliation shows high accuracy
  if (recon) {
    for (const delta of recon.featureDeltas) {
      const feature = featureMap.get(delta.featureId);
      if (feature && delta.accuracy > 0 && feature.sessions > 0) {
        feature.status = "complete";
      }
    }
  }

  return Array.from(featureMap.values()).map((f) => ({
    name: f.name,
    status: f.status,
    complexity: f.complexity,
    estimatedCost: f.estimatedCost,
    actualCost: Math.round(f.actualCost * 100) / 100,
    sessions: f.sessions,
    daysElapsed:
      f.startTime === Infinity || f.endTime === 0
        ? 0
        : Math.round(
            ((f.endTime - f.startTime) / (1000 * 60 * 60 * 24)) * 10,
          ) / 10,
  }));
}

function buildCostBreakdown(
  actuals: Actual[],
  pricingMap: Map<string, { inputPerMTok: number; outputPerMTok: number; cacheReadPerMTok: number }>,
): ReportData["costBreakdown"] {
  // By model
  const modelMap = new Map<string, { tokens: number; cost: number }>();
  const featureCostMap = new Map<string, number>();

  for (const actual of actuals) {
    const cost = calculateCost(actual, pricingMap);
    const tokens = actual.inputTokens + actual.outputTokens;

    // Accumulate by model
    const existing = modelMap.get(actual.model) ?? { tokens: 0, cost: 0 };
    existing.tokens += tokens;
    existing.cost += cost;
    modelMap.set(actual.model, existing);

    // Accumulate by feature
    const featureName = actual.featureId ?? "untagged";
    featureCostMap.set(
      featureName,
      (featureCostMap.get(featureName) ?? 0) + cost,
    );
  }

  const totalCost = Array.from(modelMap.values()).reduce(
    (sum, m) => sum + m.cost,
    0,
  );

  const byModel = Array.from(modelMap.entries())
    .map(([model, data]) => ({
      model,
      tokens: data.tokens,
      cost: Math.round(data.cost * 100) / 100,
      percentage:
        totalCost > 0 ? Math.round((data.cost / totalCost) * 100) : 0,
    }))
    .sort((a, b) => b.cost - a.cost);

  const byFeature = Array.from(featureCostMap.entries())
    .map(([feature, cost]) => ({
      feature,
      cost: Math.round(cost * 100) / 100,
      percentage: totalCost > 0 ? Math.round((cost / totalCost) * 100) : 0,
    }))
    .sort((a, b) => b.cost - a.cost);

  return { byModel, byFeature };
}

function buildVelocityData(
  features: ReportData["features"],
  snapshots: Array<{
    featuresCompleted: number;
    avgCostPerFeature: number;
    avgDaysPerFeature: number;
  }>,
): ReportData["velocity"] {
  const completedFeatures = features.filter((f) => f.status === "complete");
  const totalDays =
    completedFeatures.length > 0
      ? completedFeatures.reduce((sum, f) => sum + f.daysElapsed, 0)
      : 0;
  const totalWeeks = totalDays / 7 || 1;

  const featuresPerWeek =
    completedFeatures.length > 0
      ? Math.round((completedFeatures.length / totalWeeks) * 10) / 10
      : 0;

  const avgCostPerFeature =
    completedFeatures.length > 0
      ? Math.round(
          (completedFeatures.reduce((sum, f) => sum + f.actualCost, 0) /
            completedFeatures.length) *
            100,
        ) / 100
      : 0;

  const avgDaysPerFeature =
    completedFeatures.length > 0
      ? Math.round(
          (completedFeatures.reduce((sum, f) => sum + f.daysElapsed, 0) /
            completedFeatures.length) *
            10,
        ) / 10
      : 0;

  // Determine trend from snapshots
  let trend: "improving" | "stable" | "declining" = "stable";
  if (snapshots.length >= 2) {
    const sorted = [...snapshots].sort(
      (a, b) =>
        (a as { createdAt?: number }).createdAt ?? 0 -
        ((b as { createdAt?: number }).createdAt ?? 0),
    );
    const recent = sorted[sorted.length - 1];
    const previous = sorted[sorted.length - 2];
    if (recent.avgCostPerFeature < previous.avgCostPerFeature * 0.9) {
      trend = "improving";
    } else if (recent.avgCostPerFeature > previous.avgCostPerFeature * 1.1) {
      trend = "declining";
    }
  }

  return { featuresPerWeek, avgCostPerFeature, avgDaysPerFeature, trend };
}

function buildEstimateAccuracy(
  recon: Reconciliation | null,
  features: ReportData["features"],
): ReportData["estimateAccuracy"] {
  if (!recon) {
    // Calculate from features directly
    const withBothCosts = features.filter(
      (f) => f.estimatedCost > 0 && f.actualCost > 0,
    );
    if (withBothCosts.length === 0) {
      return { overall: 0, byComplexity: [] };
    }

    const totalEstimated = withBothCosts.reduce(
      (sum, f) => sum + f.estimatedCost,
      0,
    );
    const totalActual = withBothCosts.reduce(
      (sum, f) => sum + f.actualCost,
      0,
    );
    const overall =
      totalEstimated > 0
        ? Math.round(
            (1 - Math.abs(totalActual - totalEstimated) / totalEstimated) * 100,
          )
        : 0;

    // Group by complexity
    const byComplexity = groupByComplexity(withBothCosts);

    return { overall: Math.max(0, overall), byComplexity };
  }

  const overall = Math.round(recon.overallAccuracy * 100);

  const complexityMap = new Map<
    string,
    { totalAccuracy: number; count: number }
  >();
  for (const delta of recon.featureDeltas) {
    const feature = features.find((f) => f.name === delta.featureName);
    const complexity = feature?.complexity ?? "M";
    const existing = complexityMap.get(complexity) ?? {
      totalAccuracy: 0,
      count: 0,
    };
    existing.totalAccuracy += delta.accuracy;
    existing.count += 1;
    complexityMap.set(complexity, existing);
  }

  const byComplexity = Array.from(complexityMap.entries()).map(
    ([complexity, data]) => ({
      complexity,
      accuracy: Math.round((data.totalAccuracy / data.count) * 100),
      dataPoints: data.count,
    }),
  );

  return { overall, byComplexity };
}

function groupByComplexity(
  features: Array<{
    complexity: string;
    estimatedCost: number;
    actualCost: number;
  }>,
): Array<{ complexity: string; accuracy: number; dataPoints: number }> {
  const map = new Map<
    string,
    { totalEstimated: number; totalActual: number; count: number }
  >();

  for (const f of features) {
    const existing = map.get(f.complexity) ?? {
      totalEstimated: 0,
      totalActual: 0,
      count: 0,
    };
    existing.totalEstimated += f.estimatedCost;
    existing.totalActual += f.actualCost;
    existing.count += 1;
    map.set(f.complexity, existing);
  }

  return Array.from(map.entries())
    .map(([complexity, data]) => ({
      complexity,
      accuracy: Math.max(
        0,
        Math.round(
          (1 -
            Math.abs(data.totalActual - data.totalEstimated) /
              data.totalEstimated) *
            100,
        ),
      ),
      dataPoints: data.count,
    }))
    .sort((a, b) => {
      const order = ["S", "M", "L", "XL"];
      return order.indexOf(a.complexity) - order.indexOf(b.complexity);
    });
}
